// RomDrops Landing Page — Improved Version
import {
  AppBar,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  List,
  ListItem,
  ListItemText,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
  styled,
  Avatar,
  Divider,
} from "@mui/material";
import { Link } from "react-router-dom";
import FavoriteIcon from "@mui/icons-material/Favorite";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import VideocamIcon from "@mui/icons-material/Videocam";
import WhatshotIcon from "@mui/icons-material/Whatshot";
import { triggerEvent, EventData } from "../api/triggerEvent"; // Import the triggerEvent function

// RomDrops Color Palette
const COLORS = {
  blushRose: "#FF6BAF",
  romanceRed: "#FF4E50",
  softCream: "#FFF9F5",
  duskGray: "#444",
  silverMist: "#B8B8B8",
  honeyPeach: "#FFD3B6",
  loveLavender: "#E4C1F9",
};

const GradientTypography = styled(Typography)(({ theme }) => ({
  background: `linear-gradient(90deg, ${COLORS.romanceRed} 0%, ${COLORS.blushRose} 100%)`,
  WebkitBackgroundClip: "text",
  backgroundClip: "text",
  color: "transparent",
  display: "inline",
}));

const GradientButton = styled(Button)(({ theme }) => ({
  background: `linear-gradient(90deg, ${COLORS.romanceRed} 0%, ${COLORS.blushRose} 100%)`,
  color: "white",
  borderRadius: "50px",
  padding: theme.spacing(1, 3),
  textTransform: "none",
  fontWeight: 600,
  "&:hover": {
    background: `linear-gradient(90deg, ${COLORS.romanceRed} 0%, ${COLORS.blushRose} 100%)`,
    opacity: 0.9,
    boxShadow: theme.shadows[4],
  },
}));

const OutlineButton = styled(Button)(({ theme }) => ({
  borderRadius: "50px",
  border: `1px solid ${COLORS.romanceRed}`,
  color: COLORS.duskGray,
  padding: theme.spacing(1, 3),
  textTransform: "none",
  fontWeight: 600,
  "&:hover": {
    backgroundColor: "rgba(255, 107, 175, 0.05)",
  },
}));

const StyledCard = styled(Card)(({ theme }) => ({
  height: "100%",
  borderRadius: theme.spacing(2),
  backgroundColor: COLORS.softCream,
  boxShadow: theme.shadows[2],
  transition: "transform 0.3s ease, box-shadow 0.3s ease",
  "&:hover": {
    transform: "translateY(-5px)",
    boxShadow: theme.shadows[6],
  },
}));

const FloatingBox = styled(Box)(({ theme }) => ({
  "@keyframes float": {
    "0%, 100%": { transform: "translateY(0px)" },
    "50%": { transform: "translateY(-10px)" },
  },
  animation: "float 6s ease-in-out infinite",
}));

const AppImage = styled("img")(({ theme }) => ({

  display: "block",
  margin: "0 auto",
  [theme.breakpoints.up("sm")]: {
    maxWidth: "40%",
  },
  // Max width at md to be 40%
  [theme.breakpoints.down("sm")]: {
    maxWidth: "95%",
  },

}));

const HoverImage = styled("img")(({ theme, height = 48 }) => ({
  height: height,
  transition: "transform 0.3s ease",
  "&:hover": {
    transform: "translateY(-5px)",
  },
}));

const FeatureIcon = styled(Box)(({ theme }) => ({
  width: 60,
  height: 60,
  borderRadius: "50%",
  background: `linear-gradient(135deg, ${COLORS.loveLavender} 0%, ${COLORS.blushRose} 100%)`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: theme.spacing(2),
  color: "white",
}));

// const PremiumChip = styled(Chip)(({ theme }) => ({
//   background: `linear-gradient(90deg, ${COLORS.romanceRed} 0%, ${COLORS.blushRose} 100%)`,
//   color: "white",
//   fontWeight: "bold",
//   margin: theme.spacing(0.5),
// }));

const RomDropsLanding = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const features = [
    {
      title: "Romantic Reminders",
      subtitle: "Get alerts for the perfect moment to surprise your partner",
      icon: <FavoriteIcon sx={{ fontSize: 30 }} />,
      items: [
        "Perfect Timing Alerts",
        "Simple Gesture Ideas",
        "Never Miss a Special Moment",
      ],
    },
    {
      title: "Get Back on Track",
      subtitle: "Messed up? The app helps you make it right",
      icon: <ThumbUpIcon sx={{ fontSize: 30 }} />,
      items: ["Apology Ideas", "Comeback Plans", "Save the Weekend"],
    },
    {
      title: "Share Your Love Story",
      subtitle: "Capture reactions and create adorable videos",
      icon: <VideocamIcon sx={{ fontSize: 30 }} />,
      items: [
        "Easy Video Creator",
        "Popular Music Included",
        "Ready for Social Media",
      ],
    },
    {
      title: "Surprise Date Ideas",
      subtitle: "Fresh ideas that match your budget",
      icon: <WhatshotIcon sx={{ fontSize: 30 }} />,
      items: [
        "Budget-Friendly Options",
        "Location-Based Ideas",
        "Ready-to-Go Plans",
      ],
    },
    {
      title: "Relationship Score",
      subtitle: "See how you're doing and earn rewards",
      icon: <EmojiEventsIcon sx={{ fontSize: 30 }} />,
      items: ["Weekly Report Card", "Track Your Progress", "Earn Bonus Points"],
    },
  ];

  const testimonials = [
    {
      quote:
        "He's never been so emotionally attuned before, and the app makes it easy for him.",
      author: "Rachel, 24",
      image: "avatar1.jpg",
    },
    {
      quote:
        "I got the alert that I missed a drop moment. The redemption arc feature helped me turn it around and save the weekend.",
      author: "Josh, 28",
      image: "avatar2.jpg",
    },
    // {
    //   quote:
    //     "Our TikTok from the surprise date he planned with RomDrops got 50K views. The AI knew exactly what would make me happy.",
    //   author: "Lily, 26",
    //   image: "avatar3.jpg",
    // },
  ];

  // const pricingTiers = [
  //   {
  //     title: "Free",
  //     price: "$0",
  //     features: [
  //       "2 Romantic Alerts Monthly",
  //       "Basic Quiz",
  //       "Capture Reactions",
  //     ],
  //   },
  //   {
  //     title: "Premium",
  //     price: "$4.99/mo",
  //     features: [
  //       "Unlimited Romantic Alerts",
  //       "Video Creation Tools",
  //       "Date Night Ideas",
  //       "Relationship Score",
  //       "Apology Helpers",
  //       "Special Occasion Reminders",
  //     ],
  //   },
  // ];

  // Handler for store image clicks
  
  
  const handleStoreClick = async (
    storeType: "appStoreClick" | "playStoreClick",
    source: string
  ) => {
    const data: EventData = { source, timestamp: new Date().toISOString() };
    const result = await triggerEvent(storeType, data);
    if (!result.success) {
      console.error(result.error);
    }
  };

  return (
    <Box sx={{ fontFamily: "Segoe UI, sans-serif", background: "#fff" }}>
      {/* AppBar */}
      <AppBar
        position="fixed"
        sx={{ backgroundColor: "#fff", color: COLORS.duskGray, boxShadow: 1 }}
      >
        <Toolbar sx={{ maxWidth: 1200, mx: "auto", width: "100%" }}>
          <img
            src="logo192.png"
            alt="RomDrops Logo"
            style={{ height: 40, marginRight: 10 }}
          />
          <Typography variant="h5" fontWeight="bold" sx={{ flexGrow: 1 }}>
            <GradientTypography variant="inherit">RomDrops</GradientTypography>
          </Typography>
          {!isMobile && (
            <Box sx={{ display: "flex", gap: 4, mr: 4 }}>
              <Typography
                component="a"
                href="#features"
                sx={{
                  textDecoration: "none",
                  color: "inherit",
                  "&:hover": { opacity: 0.7 },
                }}
              >
                Features
              </Typography>
              <Typography
                component="a"
                href="#how-it-works"
                sx={{
                  textDecoration: "none",
                  color: "inherit",
                  "&:hover": { opacity: 0.7 },
                }}
              >
                How It Works
              </Typography>
              <Typography
                component="a"
                href="#stories"
                sx={{
                  textDecoration: "none",
                  color: "inherit",
                  "&:hover": { opacity: 0.7 },
                }}
              >
                Stories
              </Typography>
         
            </Box>
          )}
          <GradientButton href="#download">Get the App</GradientButton>
          <Box sx={{ m: 2 }} />
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Container
        maxWidth="md"
        sx={{ pt: { xs: 16, md: 24 }, pb: 12, textAlign: "center" }}
      >
        <Typography
          variant={isMobile ? "h4" : "h3"}
          fontWeight="bold"
          sx={{ mb: 3 }}
        >
          Never Miss a Romantic Moment Again with{" "}
          <GradientTypography variant="inherit">RomDrops</GradientTypography>
        </Typography>
        <Typography
          variant={isMobile ? "body1" : "h6"}
          color="text.secondary"
          sx={{ mb: 5, maxWidth: "800px", mx: "auto" }}
        >
          The app that tells you exactly when and how to surprise your partner
          with the perfect romantic gesture. No more guessing or forgetting -
          just happy reactions and a stronger relationship.
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "center",
            gap: 2,
            mb: 6,
          }}
        >
          <GradientButton href="#download" size="large">
            Try It Free
          </GradientButton>
          <OutlineButton href="#how-it-works" size="large">
            See It In Action
          </OutlineButton>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Available on iOS & Android
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "center", gap: 3 }}>
          <Link
            to="/early-access"
            onClick={() => handleStoreClick("appStoreClick", "hero_section")}
          >
            <HoverImage src="appstore.webp" alt="App Store" height={48} />
          </Link>
          <Link
            to="/early-access"
            onClick={() => handleStoreClick("playStoreClick", "hero_section")}
          >
            <HoverImage src="playstore.webp" alt="Google Play" height={48} />
          </Link>
        </Box>
      </Container>

      {/* App Preview */}
      <Box component="section" sx={{ bgcolor: "white" }}>
        <Container maxWidth="lg" sx={{ textAlign: "center" }}>
          <Grid
            container
            spacing={4}
            alignItems="center"
            justifyContent="center"
          >
            {/* @ts-expect-error */}
            <Grid
              item
              xs={12}
              md={4}
              sx={{ display: "flex", justifyContent: "center" }}
            >
              <FloatingBox>
                <AppImage
                  src="appScreen1.png"
                  alt="RomDrops App - AI-Timed Drops"
                />
              </FloatingBox>
            </Grid>
            {/* @ts-expect-error */}
            <Grid
              item
              xs={12}
              md={4}
              sx={{ display: "flex", justifyContent: "center" }}
            >
              <FloatingBox sx={{ animationDelay: "1s" }}>
                <AppImage
                  src="appScreen2.png"
                  alt="RomDrops App - Emotional Fitness"
                />
              </FloatingBox>
            </Grid>
            {/* @ts-expect-error */}
            <Grid
              item
              xs={12}
              md={4}
              sx={{ display: "flex", justifyContent: "center" }}
            >
              <FloatingBox sx={{ animationDelay: "2s" }}>
                <AppImage
                  src="beforeAfter.webp"
                  alt="RomDrops App - TikTok Mode"
                />
              </FloatingBox>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Key Features */}
      <Box
        id="features"
        sx={{
          py: 12,
          background: "linear-gradient(to bottom, white, #FFF0F6)",
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: 8 }}>
            <Typography
              variant={isMobile ? "h4" : "h3"}
              fontWeight="bold"
              mb={2}
            >
              How{" "}
              <GradientTypography variant="inherit">
                RomDrops
              </GradientTypography>{" "}
              Makes You a Better Partner
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ maxWidth: "800px", mx: "auto" }}
            >
              No more forgotten anniversaries or missed romantic moments
            </Typography>
          </Box>
          <Grid container spacing={4}>
            {features.map((feature, index) => (
              //@ts-expect-error
              <Grid item xs={12} sm={6} md={4} key={index}>
                <StyledCard>
                  <CardContent sx={{ p: 4, textAlign: "center" }}>
                    <FeatureIcon>{feature.icon}</FeatureIcon>
                    <GradientTypography
                      variant="h5"
                      fontWeight="bold"
                      sx={{ mb: 1 }}
                    >
                      {feature.title}
                    </GradientTypography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ mb: 3 }}
                    >
                      {feature.subtitle}
                    </Typography>
                    <List disablePadding>
                      {feature.items.map((item, i) => (
                        <ListItem
                          key={i}
                          disableGutters
                          sx={{ py: 0.5, justifyContent: "center" }}
                        >
                          <ListItemText
                            primary={item}
                            primaryTypographyProps={{
                              variant: "body2",
                              color: "text.secondary",
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </StyledCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* How It Works */}
      <Box id="how-it-works" sx={{ py: 12, bgcolor: "white" }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: 8 }}>
            <Typography
              variant={isMobile ? "h4" : "h3"}
              fontWeight="bold"
              mb={2}
            >
              How{" "}
              <GradientTypography variant="inherit">
                RomDrops
              </GradientTypography>{" "}
              Works
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ maxWidth: "800px", mx: "auto" }}
            >
              It's as easy as 1-2-3
            </Typography>
          </Box>

          <Grid container spacing={6} alignItems="center" sx={{ mb: 8 }}>
            {/* @ts-expect-error */}
            <Grid item xs={12} md={6}>
              <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>
                <GradientTypography variant="inherit">1.</GradientTypography>{" "}
                Take a Quick Quiz
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Answer a few simple questions about what your partner loves.
                Does she prefer flowers or handwritten notes? Surprise outings
                or cozy nights in? The app learns what makes her happy.
              </Typography>
              <List>
                {[
                  "Takes just 5 minutes",
                  "No relationship expertise needed",
                  "Easy to update anytime",
                ].map((item, i) => (
                  <ListItem key={i} sx={{ py: 1 }}>
                    <ListItemText primary={item} />
                  </ListItem>
                ))}
              </List>
            </Grid>

            {/* <Grid item xs={12} md={6}>
              <Box sx={{ p: 2, borderRadius: 3, bgcolor: COLORS.softCream }}>
                <img src="/api/placeholder/500/300" alt="App Onboarding" style={{ width: "100%", borderRadius: 16 }} />
              </Box>
            </Grid> */}
          </Grid>

          <Grid
            container
            spacing={6}
            alignItems="center"
            sx={{ mb: 8 }}
            direction={isMobile ? "column-reverse" : "row"}
          >
            {/* <Grid item xs={12} md={6}>
              <Box sx={{ p: 2, borderRadius: 3, bgcolor: COLORS.softCream }}>
                <img src="/api/placeholder/500/300" alt="Drop Generation" style={{ width: "100%", borderRadius: 16 }} />
              </Box>
            </Grid> */}
            {/* @ts-expect-error */}
            <Grid item xs={12} md={6}>
              <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>
                <GradientTypography variant="inherit">2.</GradientTypography>{" "}
                Get Perfectly Timed Alerts
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                The app sends you notifications when it's the perfect time for a
                romantic gesture. "She dressed up today!" or "She had a tough
                day at work." Each alert comes with easy gesture ideas you can
                do right away.
              </Typography>
              <List>
                {[
                  "Simple notifications on your phone",
                  "Perfect timing based on your calendar",
                  "Ready-to-use romantic ideas",
                ].map((item, i) => (
                  <ListItem key={i} sx={{ py: 1 }}>
                    <ListItemText primary={item} />
                  </ListItem>
                ))}
              </List>
            </Grid>
          </Grid>

          <Grid container spacing={6} alignItems="center" sx={{ mb: 8 }}>
            {/* @ts-expect-error */}
            <Grid item xs={12} md={6}>
              <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>
                <GradientTypography variant="inherit">3.</GradientTypography>{" "}
                Become a Relationship Hero
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Make her day with perfectly timed gestures. Capture her reaction
                and share it with friends. Track your progress and see your
                relationship grow stronger. It's like having a relationship
                coach in your pocket!
              </Typography>
              <List>
                {[
                  "See your relationship score improve",
                  "Never miss important moments",
                  "Capture priceless reactions",
                ].map((item, i) => (
                  <ListItem key={i} sx={{ py: 1 }}>
                    <ListItemText primary={item} />
                  </ListItem>
                ))}
              </List>
            </Grid>

            {/* <Grid item xs={12} md={6}>
              <Box sx={{ p: 2, borderRadius: 3, bgcolor: COLORS.softCream }}>
                <img src="/api/placeholder/500/300" alt="Emotional Fitness" style={{ width: "100%", borderRadius: 16 }} />
              </Box>
            </Grid> */}
          </Grid>

          <Grid
            container
            spacing={6}
            alignItems="center"
            direction={isMobile ? "column-reverse" : "row"}
          >
            {/* <Grid item xs={12} md={6}>
              <Box sx={{ p: 2, borderRadius: 3, bgcolor: COLORS.softCream }}>
                <img src="/api/placeholder/500/300" alt="TikTok Story Mode" style={{ width: "100%", borderRadius: 16 }} />
              </Box>
            </Grid> */}
            {/* @ts-expect-error */}
            <Grid item xs={12} md={6}>
              <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>
                <GradientTypography variant="inherit">4.</GradientTypography>{" "}
                Share Your Sweet Moments
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Capture her reaction when you surprise her with flowers or a
                sweet note. The app helps you create adorable videos that are
                perfect for TikTok or Instagram - complete with music and
                captions.
              </Typography>
              <List>
                {[
                  "One-tap video creation",
                  "Popular music options included",
                  "Easy to share on social media",
                ].map((item, i) => (
                  <ListItem key={i} sx={{ py: 1 }}>
                    <ListItemText primary={item} />
                  </ListItem>
                ))}
              </List>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Testimonials */}
      <Box
        id="stories"
        sx={{
          py: 12,
          background: "linear-gradient(to bottom, #FFF0F6, white)",
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant={isMobile ? "h4" : "h3"}
            fontWeight="bold"
            sx={{ mb: 6, textAlign: "center" }}
          >
            Real{" "}
            <GradientTypography variant="inherit">
              Love Stories
            </GradientTypography>
          </Typography>

          <Grid container spacing={4} justifyContent="center">
            {testimonials.map((t, i) => (
              //@ts-expect-error
              <Grid item xs={12} sm={8} md={6} key={i}>
                <StyledCard sx={{ p: 1 }}>
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                      <Avatar
                        src={t.image}
                        sx={{ width: 60, height: 60, mr: 2 }}
                      />
                      <Typography variant="h6" fontWeight="bold">
                        {t.author}
                      </Typography>
                    </Box>
                    <Divider sx={{ mb: 3 }} />
                    <Typography variant="body1" color="text.secondary">
                      "{t.quote}"
                    </Typography>
                  </CardContent>
                </StyledCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* TikTok Embed */}
      {/* <Box sx={{ py: 12, bgcolor: "white", textAlign: "center" }}>
        <Container maxWidth="md">
          <Typography variant={isMobile ? "h4" : "h3"} fontWeight="bold" sx={{ mb: 4 }}>
            See <GradientTypography variant="inherit">RomDrops</GradientTypography> in Action
          </Typography>
          <Grid container spacing={4} justifyContent="center">
          
            <Grid item xs={12} sm={6}>
              <Box component="iframe" src="https://www.tiktok.com/embed/7493705564133018926" sx={{ width: "100%", height: 600, border: "none", borderRadius: 2, boxShadow: 3 }} allow="autoplay; encrypted-media" />
            </Grid>
          
            <Grid item xs={12} sm={6} sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "flex-start", textAlign: "left" }}>
              <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>
                Real Reactions to <GradientTypography variant="inherit">RomDrops</GradientTypography> Surprises
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                Watch her face light up when he gets it right! These couples used RomDrops to create sweet, memorable moments that were easy to plan and perfect to share.
              </Typography>
              <List>
                {["No acting here - just genuine surprise", "Simple gestures with big impact", "Moments worth remembering"].map((item, i) => (
                  <ListItem key={i} sx={{ py: 1 }}>
                    <ListItemText primary={item} />
                  </ListItem>
                ))}
              </List>
            </Grid>
          </Grid>
        </Container>
      </Box> */}

      {/* Download CTA */}
      <Box id="download" sx={{ py: 12, textAlign: "center", bgcolor: "white" }}>
        <Container maxWidth="md">
          <Typography
            variant={isMobile ? "h4" : "h3"}
            fontWeight="bold"
            sx={{ mb: 2 }}
          >
            Start Your{" "}
            <GradientTypography variant="inherit">RomDrops</GradientTypography>{" "}
            Journey
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ mb: 5, maxWidth: "800px", mx: "auto" }}
          > 
            Download now to transform your relationship with AI-powered
            surprises, emotionally intelligent timing, and relationship-building
            games.
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "center", gap: 3 }}>
            <Link
              to="/early-access"
              onClick={() =>
                handleStoreClick("appStoreClick", "download_section")
              }
            >
              <HoverImage src="appstore.webp" alt="App Store" height={56} />
            </Link>
            <Link
              to="/early-access"
              onClick={() =>
                handleStoreClick("playStoreClick", "download_section")
              }
            >
              <HoverImage src="playstore.webp" alt="Google Play" height={56} />
            </Link>
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <Box
        component="footer"
        sx={{ bgcolor: "grey.100", py: 4, textAlign: "center" }}
      >
        <Container maxWidth="md">
          <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
            <img src="logo192.png" alt="RomDrops Logo" style={{ height: 40 }} />
            <Typography variant="h5" fontWeight="bold" sx={{ ml: 1 }}>
              <GradientTypography variant="inherit">
                RomDrops
              </GradientTypography>
            </Typography>
          </Box>
          <Box
            sx={{ display: "flex", justifyContent: "center", gap: 4, mb: 3 }}
          >
            <Typography
              component="a"
              href="#features"
              sx={{ textDecoration: "none", color: COLORS.duskGray }}
            >
              Features
            </Typography>
            <Typography
              component="a"
              href="#how-it-works"
              sx={{ textDecoration: "none", color: COLORS.duskGray }}
            >
              How It Works
            </Typography>
            <Typography
              component="a"
              href="#stories"
              sx={{ textDecoration: "none", color: COLORS.duskGray }}
            >
              Stories
            </Typography>
   
          </Box>
          <Typography variant="body2" color="text.secondary">
            © 2025 RomDrops. All rights reserved.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default RomDropsLanding;
